import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PLATFORMS = [
  { key: 'pinterest', label: 'Pinterest', icon: 'Image', accentColor: '#E60023' },
  { key: 'facebook', label: 'Facebook', icon: 'Users', accentColor: '#1877F2' },
  { key: 'threads', label: 'Threads', icon: 'AtSign', accentColor: '#000000' }
];

const DayBar = ({ day, max, accentColor }) => {
  const pct = max > 0 ? Math.max((day.count / max) * 100, 6) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-[#64748B] w-11 flex-shrink-0">{day.label}</span>
      <div className="flex-1 min-w-0 h-[7px] rounded-full" style={{ backgroundColor: `${accentColor}15` }}>
        <div
          className="h-[7px] rounded-full"
          style={{ width: `${pct}%`, backgroundColor: accentColor }}
        />
      </div>
      <span className="text-[11px] font-semibold text-[#0F172A] w-5 text-right flex-shrink-0">{day.count}</span>
    </div>
  );
};

const AccountWorkloadCard = ({ account, accentColor, onViewInsights }) => {
  const maxCount = account.days.reduce((m, d) => Math.max(m, d.count), 0);

  return (
    <div className="bg-white rounded-lg border border-[#F1F5F9] p-3.5 w-[260px] flex-shrink-0 snap-start">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: accentColor }} />
        <span className="text-xs font-semibold text-[#0F172A] truncate">{account.name}</span>
      </div>

      {account.total === 0 ? (
        <div className="flex items-center gap-1.5 py-3 text-[#94A3B8]">
          <Icon name="CalendarOff" size={13} />
          <span className="text-[11px]">Nothing scheduled</span>
        </div>
      ) : (
        <>
          <p className="text-[11px] text-[#64748B] mb-2.5">
            <span className="font-semibold text-[#0F172A]">{account.total}</span> posts scheduled through{' '}
            <span className="font-medium text-[#0F172A]">{account.lastDate}</span>
          </p>
          <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: 180 }}>
            {account.days.map((day) => (
              <DayBar key={day.date} day={day} max={maxCount} accentColor={accentColor} />
            ))}
          </div>
        </>
      )}
      {onViewInsights && (
        <button
          type="button"
          onClick={onViewInsights}
          className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold hover:opacity-80 transition-opacity"
          style={{ color: accentColor }}
        >
          View Insights
          <Icon name="ArrowUpRight" size={11} />
        </button>
      )}
    </div>
  );
};

const SectionSkeleton = () => (
  <div className="border-t border-[#F1F5F9] px-5 py-4 animate-pulse">
    <div className="h-4 bg-slate-100 rounded w-32 mb-3" />
    <div className="flex gap-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="w-[260px] h-28 bg-slate-100 rounded-lg flex-shrink-0" />
      ))}
    </div>
  </div>
);

const PlatformSection = ({ platform, accounts, onViewInsights }) => {
  const [expanded, setExpanded] = useState(true);
  const totalPosts = accounts.reduce((sum, a) => sum + a.total, 0);

  return (
    <div className="border-t border-[#F1F5F9]">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#FAFAFA] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${platform.accentColor}15` }}
          >
            <Icon name={platform.icon} size={13} style={{ color: platform.accentColor }} />
          </div>
          <span className="text-sm font-semibold text-[#0F172A]">{platform.label}</span>
          {accounts.length > 0 && (
            <span className="text-[11px] text-[#94A3B8]">
              {accounts.length} account{accounts.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {totalPosts > 0 && (
            <span
              className="text-xs font-medium px-2.5 py-1 rounded-full border"
              style={{ color: platform.accentColor, backgroundColor: `${platform.accentColor}10`, borderColor: `${platform.accentColor}30` }}
            >
              {totalPosts} scheduled
            </span>
          )}
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-[#94A3B8]" />
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-4">
          {accounts.length === 0 ? (
            <p className="text-[11px] text-[#94A3B8] py-2">No connected accounts</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto snap-x pb-1">
              {accounts.map((account) => (
                <AccountWorkloadCard
                  key={account.id}
                  account={account}
                  accentColor={platform.accentColor}
                  onViewInsights={() => onViewInsights?.(platform.key, account.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ScheduledWorkloadPanel = ({ data, loading, onViewInsights }) => {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
      <div className="px-5 py-4">
        <h3 className="text-sm font-semibold text-[#0F172A]">Posting Forecast</h3>
        <p className="text-[11px] text-[#94A3B8] mt-0.5">Scheduled workload across all connected accounts, today onward</p>
      </div>

      {loading ? (
        <>
          <SectionSkeleton />
          <SectionSkeleton />
          <SectionSkeleton />
        </>
      ) : (
        PLATFORMS.map((platform) => (
          <PlatformSection
            key={platform.key}
            platform={platform}
            accounts={data?.[platform.key]?.accounts || []}
            onViewInsights={onViewInsights}
          />
        ))
      )}
    </div>
  );
};

export default ScheduledWorkloadPanel;
